import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

import type { CaraWorkspaceEmailTemplateUse } from '~/api/graphql';
import type { EmailTemplateChoice } from '~/api/data/email-templates/queries';
import type { DraftTemplateState } from './hooks/use-composer-email-templates';

const mutedTextClassName = 'text-sm/6 text-neutral-500 dark:text-neutral-400';

type TemplateUse = Pick<CaraWorkspaceEmailTemplateUse, 'templateId'>;

const DraftTemplateName = ({
  draftTemplate,
  isOlderVersion
}: {
  draftTemplate: DraftTemplateState;
  isOlderVersion: boolean;
}) => {
  switch (draftTemplate.status) {
    case 'loading':
      return <Spinner size="size-4" color="neutral" />;
    case 'hidden':
      return <span className={mutedTextClassName}>A template you can't see</span>;
    case 'failed':
      return <span className={mutedTextClassName}>We couldn't load this template.</span>;
    case 'loaded':
      return (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate text-sm/6 font-medium text-neutral-950 dark:text-white">
            {draftTemplate.template.name}
          </span>
          {draftTemplate.template.archivedAt && <Badge color="neutral">Archived</Badge>}
          {isOlderVersion && <Badge color="amber">Older version</Badge>}
        </span>
      );
  }
};

/** What the composer's Template row says about the template this email was drafted from. */
export const EmailTemplateRowContent = (props: {
  templateUse: TemplateUse | null | undefined;
  draftTemplate: DraftTemplateState;
  /** Undefined until the agency's templates have loaded. */
  choices: EmailTemplateChoice[] | undefined;
  /** Whether the template changed after this email was drafted from it. */
  isOlderVersion: boolean;
  hasChoicesLoadFailed: boolean;
  onRetryChoices: () => void;
}) => {
  if (props.templateUse) {
    return (
      <DraftTemplateName
        draftTemplate={props.draftTemplate}
        isOlderVersion={props.isOlderVersion}
      />
    );
  }
  if (props.hasChoicesLoadFailed) {
    return (
      <span className="flex items-center gap-2">
        <span className={mutedTextClassName}>We couldn't load your templates.</span>
        <Button plain onClick={props.onRetryChoices}>
          Try again
        </Button>
      </span>
    );
  }
  const hasNoTemplatesToPick = props.choices?.length === 0;
  return (
    <span className={mutedTextClassName}>
      {hasNoTemplatesToPick ? 'No templates yet. Add one in Library.' : 'No template'}
    </span>
  );
};
